import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Loader2 } from 'lucide-react';
import { useEvent, useLazyQuery } from '@soap/modules';
import { AssessmentQuestionReview } from './AssessmentQuestionReview';

interface AssessmentReviewSectionProps {
    assessmentResultId: string;
    assessmentResults: Map<string, any>;
    setAssessmentResults: React.Dispatch<React.SetStateAction<Map<string, any>>>;
    isEditable?: boolean;
}

export function AssessmentReviewSection({ 
    assessmentResultId, 
    assessmentResults, 
    setAssessmentResults,
    isEditable = true
}: AssessmentReviewSectionProps) {
    const [getAssessmentResult, gotAssessmentResult] = useLazyQuery("C219v1_GetAssessmentResult", {
        C219_AssessmentResultId: assessmentResultId
    });

    useEffect(() => {
        if (!assessmentResults.has(assessmentResultId)) {
            getAssessmentResult();
        }
    }, [assessmentResultId]);

    useEffect(() => {
        if (gotAssessmentResult) {
            setAssessmentResults(prev => {
                const newMap = new Map(prev);
                newMap.set(assessmentResultId, gotAssessmentResult);
                return newMap;
            });
        }
    }, [gotAssessmentResult, assessmentResultId, setAssessmentResults]);

    useEvent({
        eventName: "E179v1_QuestionMarked",
        onEventReceived: (event: any) => {
            const currentResult = assessmentResults.get(assessmentResultId);
            if (currentResult) {
                const cloned = JSON.parse(JSON.stringify(currentResult));
                cloned.e177_StudentScoreAsPercentage = event.e179_AssessmentScore;
                const question = cloned.e177_Questions.find((q: any) => q.e177_QuestionId === event.e179_QuestionId);
                if (question) {
                    question.e177_PointsGiven = event.e179_QuestionScore;
                }
                setAssessmentResults(prev => {
                    const newMap = new Map(prev);
                    newMap.set(assessmentResultId, cloned);
                    return newMap;
                });
            }
        }
    }, [assessmentResultId, assessmentResults]);

    const result = assessmentResults.get(assessmentResultId);

    if (!result) {
        return (
            <Card className="mt-4">
                <CardContent className="p-4">
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                        <span className="ml-2 text-sm text-gray-500">Loading assessment results...</span>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="mt-4 border-2 border-confirm-action/30">
            <CardHeader>
                <CardTitle className="text-lg">Assessment Review</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Score Section */}
                <div className="text-center space-y-4">
                    <div className="space-y-2">
                        <h3 className="text-2xl font-bold text-gray-900">
                            Score: {result.e177_StudentScoreAsPercentage ?? 0}%
                        </h3>
                    </div>
                </div>

                {/* Questions Section */}
                <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900">Questions</h4>
                    <div className="space-y-4">
                        {result.e177_Questions?.map((q: any, i: number) => (
                            <AssessmentQuestionReview 
                                key={i} 
                                index={i} 
                                assessmentResultId={assessmentResultId} 
                                question={q}
                                isEditable={isEditable}
                            />
                        ))}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}





